import * as api from "./client";

// ============================================================================
// Types
// ============================================================================

export type TemplateType =
  | "newsletter"
  | "website"
  | "intranet"
  | "press_release"
  | "email"
  | "social_1"
  | "social_2"
  | "social_3";

export type EntityScope = "firm" | "set";

export type RankingBand =
  | "any"
  | "top_tier"
  | "recommended_firm"
  | "firm_to_watch"
  | "accolade"
  | "recommended_lawyer";

export type Language = "en" | "de" | "fr";

export type CopyTemplate = {
  id: string;
  templateType: TemplateType;
  entityScope: EntityScope;
  rankingBand: RankingBand;
  language: Language;
  content: string;
  version: number;
  updatedAt: string;
  updatedBy: string | null;
};

export type CopyTemplateListFilters = {
  entityScope?: EntityScope;
  language?: Language;
  rankingBand?: RankingBand;
};

export type SaveCopyTemplateInput = {
  templateType: TemplateType;
  entityScope: EntityScope;
  rankingBand: RankingBand;
  language: Language;
  content: string;
  /** The version the editor loaded. A save is refused when the stored row moved on. */
  expectedVersion?: number;
};

export type CopyTemplateListResponse = {
  templates: CopyTemplate[];
  requestId?: string | null;
};

export type CopyTemplateResponse = {
  template: CopyTemplate;
  requestId?: string | null;
};

export type CopyTemplatePreviewInput = {
  entityScope: EntityScope;
  language: Language;
  rankingBand: RankingBand;
  content: string;
};

export type CopyTemplatePreviewResponse = {
  html: string;
  values: Record<string, string>;
  source: "sample";
  requestId?: string | null;
};

export type Placeholder = {
  name: string;
  label: string;
  description: string;
};

export type PlaceholderListResponse = {
  placeholders: Placeholder[];
  requestId?: string | null;
};

// ============================================================================
// Service
// ============================================================================

function buildListParams(filters?: CopyTemplateListFilters): string {
  const params = new URLSearchParams();
  if (filters?.entityScope) params.append("entityScope", filters.entityScope);
  if (filters?.language) params.append("language", filters.language);
  if (filters?.rankingBand) params.append("rankingBand", filters.rankingBand);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const copyTemplates = {
  list: async (filters?: CopyTemplateListFilters): Promise<CopyTemplateListResponse> => {
    return api.get<CopyTemplateListResponse>(`/v1/copy-templates${buildListParams(filters)}`);
  },

  save: async (input: SaveCopyTemplateInput): Promise<CopyTemplateResponse> => {
    return api.put<CopyTemplateResponse>("/v1/copy-templates", input);
  },

  preview: async (input: CopyTemplatePreviewInput): Promise<CopyTemplatePreviewResponse> => {
    return api.post<CopyTemplatePreviewResponse>("/v1/copy-templates/preview", input);
  },

  placeholders: async (): Promise<PlaceholderListResponse> => {
    return api.get<PlaceholderListResponse>("/v1/copy-templates/placeholders");
  },
};
